import { CommonModule } from "@angular/common";
import { Component, OnDestroy, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { IonContent, IonicModule } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, firstValueFrom, of } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";

//Services
import { EventsService } from "@providers/EventsService";
import { NewBaseService } from "@providers/newBase.service";

import { DeviceService } from "@services/device.service";
import { SportNavigationService, SportService } from "@sports-services/index";

//Models
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { C_Country } from "@sports-models/index";
import { C_League } from "@sports-models/index";

// Stores
import { NewTicketFacade } from "@sports-features/ticket/stores";
import { SportsbookState } from "@sports-stores/sportsbook/index";
import { SportsbookFacade } from "@sports-stores/sportsbook/sportsbook.facade";
import * as sportsbookSelector from "@sports-stores/sportsbook/sportsbook.selectors";

// Components
import { CodereFooterComponent } from "@components/mso/footer/codere-footer/codere-footer";
import { SbSimpleNavbarComponent, SbTabsComponent } from "@sports-components/index";

//Others
import { SPORTS_PATHS } from "@shared-constants/routes";
import { SportsBasePage, TrackingInfo } from "@sports-pages/index";

@Component({
  selector: "sport-countries-page",
  templateUrl: "./sport-countries.page.html",
  styleUrls: ["./sport-countries.page.scss"],
  standalone: true,
  imports: [
    IonicModule,
    CommonModule,
    FormsModule,
    SbSimpleNavbarComponent,
    TranslateModule,
    CodereFooterComponent,
    SbTabsComponent,
  ],
})
export class SportCountriesPage extends SportsBasePage implements OnDestroy {
  @ViewChild(IonContent) content: IonContent;
  highlights$: Observable<any>;
  countries$: Observable<any>;
  currentCountry$ = new BehaviorSubject<C_Country | null>(null);
  currentFilter$ = new BehaviorSubject<any>(null);
  filters: any[];
  sport: C_League;
  sportHandle: any;
  groupTitle: string;
  sportService = inject(SportService);
  newTicketFacade = inject(NewTicketFacade);
  translate = inject(TranslateService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  newBaseService = inject(NewBaseService);
  globals: GlobalVarsModel;
  private eventService = inject(EventsService);
  currentId: number;
  deviceService = inject(DeviceService);
  isMobile = false;
  isDesktop = false;
  public sportsbookFacade = inject(SportsbookFacade);
  sportsbookStore = inject(Store<SportsbookState>);
  sportCountriesData$: Observable<any>;
  sportNavigationService = inject(SportNavigationService);

  sportCountriesDataSub$: Subscription;

  countriesData$: Observable<any>;

  selectedCountry = undefined;
  initialValueDone = false;

  sportsNavigationId = undefined;

  stateToSave = {
    selectedCountry: undefined,
    currentFilter: undefined,
    scrollPosition: undefined,
  };
  stateToRestore = undefined;

  private ngUnsubscribe$ = new Subject<boolean>();

  constructor() {
    super();
    this.isDesktop = this.deviceService.isDesktop();
    this.isMobile = this.deviceService.isMobile();

    const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
    this.sport = currentUrl.state?.params;
    this.sportsNavigationId = currentUrl.state?.sportsNavigationId;
    this.groupTitle = this.sport?.Name === "Tenis" ? "Torneos" : this.translate.instant("cPAz");
    this.setGroupTitle(this.sport.Name);
    this.sportsbookFacade.getSportCountriesData(this.sport);

    this.newBaseService.getVars
      .pipe(
        switchMap((data: GlobalVarsModel) => {
          this.globals = data;
          return of(null);
        }),
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe();

    this.sportCountriesData$ = this.sportsbookStore.select(sportsbookSelector.getSportCountriesDataState);
    this.sportCountriesDataSub$ = this.sportCountriesData$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((sportCountriesData) => {
        this.countriesData$ = of(sportCountriesData?.countries);
        this.highlights$ = of(sportCountriesData?.highlights);
        this.setupFilters(sportCountriesData?.highlights?.length > 0);
        this.setCountries();
        const currentUrl = this.sportNavigationService.navigationState$.getValue().currentUrl;
        if (currentUrl?.popstate && sportCountriesData?.countries?.length && !this.initialValueDone) {
          this.sportsNavigationId = currentUrl.state.sportsNavigationId;
          this.stateToRestore = this.sportNavigationService.getRestoreStateFromHistory(this.sportsNavigationId);
          this.stateToSave = this.stateToRestore;
          this.setInitialValue();
        }
      });
  }

  private setGroupTitle(sport) {
    switch (sport?.Name) {
      case "Tenis":
        this.groupTitle = "Torneos";
        break;
      case "Olimpiadas":
        this.groupTitle = "Deportes";
        break;
      default:
        this.groupTitle = this.translate.instant("cPAz");
        break;
    }
  }

  private setupFilters(showHighlights) {
    this.filters = [{ index: 0, name: this.translate.instant("cPAz"), navigate: "az", count: 1 }];
    if (showHighlights)
      this.filters.push({ index: 1, name: this.translate.instant("cPDest"), navigate: "highlight", count: 1 });
    if (!this.currentFilter$.getValue()) this.currentFilter$.next(this.filters[0]);
  }

  private setCountries() {
    this.countries$ = combineLatest([
      this.currentCountry$.asObservable(),
      this.countriesData$.pipe(map((countries) => countries.map((country) => ({ ...country, Selected: false })))),
    ]).pipe(
      map(([current, countries]) => {
        if (!current) return countries;
        return countries.map((country) => ({
          ...country,
          Selected: country.Selected ? false : country.NodeId === current.NodeId,
        }));
      }),
    );
  }

  private setInitialValue() {
    if (this.stateToRestore) {
      this.stateToSave = this.stateToRestore;
      let targetScroll;
      let targetSelectedCountry;
      let targetFilter;
      if (this.stateToRestore.currentFilter) {
        targetFilter = this.stateToRestore.currentFilter;
      }
      if (this.stateToRestore.selectedCountry) {
        targetSelectedCountry = this.stateToRestore.selectedCountry;
      }
      if (this.stateToRestore.scrollPosition?.[targetFilter.name]) {
        targetScroll = this.stateToRestore.scrollPosition?.[targetFilter.name];
      } else {
        targetScroll = 0;
      }
      if (!targetScroll) {
        this.currentFilter$.next(targetFilter);
        this.selectedCountry = targetSelectedCountry;
        this.content.scrollToTop();
      } else {
        this.currentFilter$.next(targetFilter);
        setTimeout(() => {
          this.content.scrollToPoint(undefined, targetScroll, 150);
          setTimeout(() => {
            this.selectedCountry = targetSelectedCountry;
          }, 151);
        }, 50);
      }
    }
    this.initialValueDone = true;
  }

  selectedCountryChange(selection) {
    if (selection?.detail?.value) {
      this.selectedCountry = +selection?.detail?.value;
      this.stateToSave = {
        ...this.stateToSave,
        selectedCountry: this.selectedCountry,
        currentFilter: this.currentFilter$.getValue(),
      };
    }
  }

  selectedFilterChange(event) {
    this.currentFilter$.next(event);
    this.stateToSave = {
      ...this.stateToSave,
      currentFilter: event,
    };
    const position = this.stateToSave.scrollPosition?.[event.name] || 0;
    this.content.scrollToPoint(undefined, position, 150);
  }

  async navigate(league: any, highlight?: boolean, country?: any) {
    if (country) {
      league = { ...league, CountryCode: country.CountryCode, SportName: this.sport.Name };
    }
    let componentName = SPORTS_PATHS.EventoPage;
    let options: any = { league: league, hasBackButton: true };
    let event = highlight ? "SelectLeague_Destacado" : "SelectLeague";

    const data = await firstValueFrom(this.sportService.getEvents({ parentId: league.NodeId }));
    if (data.length === 1) {
      componentName = data[0].isLive ? SPORTS_PATHS.NowLiveDetailPage : SPORTS_PATHS.MercadosPage;
      options = { market: data[0] };
      event = data[0].isLive ? "pushEventLive" : "pushEvent";
    }

    const hasBackButton: string = this.router.routerState.snapshot.url;
    this.eventService.sendCanGoBack(hasBackButton === `/${SPORTS_PATHS.SportCountriesPage}`);

    const urlComponent = `/${componentName}`;
    this.router.navigate([urlComponent], {
      state: { params: options.league || options.market },
    });

    this.trackingService.trackEvent([event, league.Name, league.SportHandle, league.NodeId, "event"]);
  }

  trackBy(item: C_Country) {
    return item.NodeId;
  }

  onScroll(ev) {
    const scrollPositionRest = this.stateToSave.scrollPosition ? this.stateToSave.scrollPosition : {};
    this.stateToSave = {
      ...this.stateToSave,
      scrollPosition: {
        ...scrollPositionRest,
        [this.currentFilter$.getValue().name]: ev.detail.scrollTop,
      },
    };
    this.stateToSave.scrollPosition[this.currentFilter$.getValue().name] = ev.detail.scrollTop;
  }

  getTrackingInfo(): TrackingInfo {
    return {
      uri: encodeURI(`/${this.sport.SportHandle}`),
      description: `Deporte - ${this.sport.SportHandle}`,
      additionalData: {
        nodeId: this.sport.NodeId,
        sportHandle: this.sport.SportHandle,
      },
    };
  }

  ngOnDestroy() {
    this.sportNavigationService.injectLastStateToHistory(this.sportsNavigationId, this.stateToSave);
    this.ngUnsubscribe$.next(true);
    this.ngUnsubscribe$.complete();
  }
}
